// Personal, client-only achievements for the God Mode easter egg. Persisted in localStorage (per
// browser, no backend, no leaderboard) so nothing about a user's activity is ever stored centrally.
// Tracking only switches on once the user has discovered any easter egg (the `discovered` flag), so
// regular staff who never type a code never see achievement toasts.
const KEY = 'god_mode_achievements';
const EVENT = 'god_mode_achievements_changed';

interface AchState {
    discovered: boolean;
    unlocked: string[];
    day: string; // YYYY-MM-DD the per-day counters below belong to
    pageVisits: Record<string, number>;
}

// Local YYYY-MM-DD (not UTC): the per-day counters must roll over on the same local-midnight boundary
// the time-of-day achievements already use, otherwise "today" resets early/late near midnight.
const today = (): string => {
    const d = new Date();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
};

function read(): AchState {
    try {
        const raw = localStorage.getItem(KEY);
        if (raw) {
            const s = JSON.parse(raw) as Partial<AchState>;
            const day = s.day === today() ? s.day : today();
            return {
                discovered: !!s.discovered,
                unlocked: s.unlocked ?? [],
                day,
                pageVisits: s.day === today() ? s.pageVisits ?? {} : {},
            };
        }
    } catch {
        /* corrupt or unavailable storage — fall through to defaults */
    }
    return { discovered: false, unlocked: [], day: today(), pageVisits: {} };
}

function write(s: AchState): void {
    try {
        localStorage.setItem(KEY, JSON.stringify(s));
    } catch {
        /* storage full/unavailable — achievements are non-essential, ignore */
    }
    window.dispatchEvent(new CustomEvent(EVENT));
}

export const achievementsStore = {
    isTrackingEnabled: (): boolean => read().discovered,

    markDiscovered: (): void => {
        const s = read();
        if (!s.discovered) {
            s.discovered = true;
            write(s);
        }
    },

    has: (id: string): boolean => read().unlocked.includes(id),

    getUnlocked: (): string[] => read().unlocked,

    unlock: (id: string): void => {
        const s = read();
        if (!s.unlocked.includes(id)) {
            s.unlocked.push(id);
            write(s);
        }
    },

    // Records a visit to `path` for today and returns the running count (for the Déjà Vu achievement).
    bumpPageVisit: (path: string): number => {
        const s = read();
        const n = (s.pageVisits[path] ?? 0) + 1;
        s.pageVisits[path] = n;
        write(s);
        return n;
    },

    subscribe: (cb: () => void): (() => void) => {
        window.addEventListener(EVENT, cb);
        return () => window.removeEventListener(EVENT, cb);
    },
};
